import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container,
  Typography, 
  Button, 
  Stack, 
  Card, 
  Divider,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import PageHero from '../../components/PageHero/PageHero';
import TeamCarousel from '../../components/TeamCarousel/TeamCarousel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import VerifiedIcon from '@mui/icons-material/Verified';
import SchoolIcon from '@mui/icons-material/School';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import StarIcon from '@mui/icons-material/Star';

interface TeamMember {
  id: number;
  slug: string;
  name: string;
  role: string;
  shortBio: string;
  fullBio: string;
  detailedBio?: string;
  image: string;
  quote?: string;
  badge?: {
    text: string;
    color: string;
  };
  stats?: {
    title: string;
    description: string;
    icon: React.ReactNode;
  }[];
  linkedIn?: string;
  achievements?: string[];
  expertise?: string[];
  yearsOfExperience?: string;
}

const teamMembers: TeamMember[] = [
  {
    id: 1,
    slug: 'arnaldo-llerena',
    name: 'Arnaldo Llerena',
    role: 'Fundador',
    image: '/Arnaldo Llerena.jpg',
    quote: 'Nuestra misión es hacer que los alimentos ecológicos sean accesibles para todos, transformando la agricultura hacia un modelo más sostenible y regenerativo.',
    shortBio: 'Pionero en impulsar técnicas de agricultura ecológica desde los años 90.\nOrganiza cursos y talleres con expertos nacionales e internacionales.\nTransforma la agricultura hacia un modelo sostenible y regenerativo.',
    fullBio: 'Pionero en impulsar técnicas de agricultura ecológica desde los años 90. Organiza cursos y talleres con expertos nacionales e internacionales. Transforma la agricultura hacia un modelo sostenible y regenerativo.',
    detailedBio: 'Arnaldo, médico especializado en Rehabilitación, jubilado; tiene pasión por difundir conocimiento que aporte valor a la humanidad. Descubrió la agricultura sostenible en la década de los 90 con la Permacultura. En 1996 organizó el primer curso intensivo de Permacultura en Firgas, Gran Canaria, junto a Richard Wade e Inés Sánchez. En 1999, junto a su esposa Beatriz Cárdenas, adquirió una finca de 30.000 m² con un invernadero de 3.000 m² en Valencia, transformando su pasión en Ekkofood: producción ecológica accesible para todos, mientras organizaban cursos y talleres.',
    stats: [
      {
        title: 'Agricultura Ecológica',
        description: 'Impulsó técnicas sostenibles y regenerativas desde la década de los 90.',
        icon: <AgricultureIcon sx={{ color: '#455b40' }} />
      },
      {
        title: 'Formación y Talleres',
        description: 'Organizó cursos pioneros con expertos internacionales en España y Perú.',
        icon: <SchoolIcon sx={{ color: '#455b40' }} />
      }
    ],
    achievements: [
      'Primer curso intensivo de Permacultura en Firgas, Gran Canaria (1996)',
      'Fundador de Ekkofood (1999)',
      'Primeros cursos con Jairo Restrepo en España y Perú (2015 -2018)',
      'Primer curso del método JADAM con Younsang Cho en España (2019)',
      'Primeros cursos del Modelo Biofábrica con Pedro Isabeles en España y Perú 2024 - 2025)'
    ],
    expertise: ['Permacultura', 'Agricultura Ecológica', 'Métodos Regenerativos'],
    yearsOfExperience: 'Fundador'
  },
  {
    id: 2,
    slug: 'beatriz-cardenas',
    name: 'Beatriz Cárdenas',
    role: 'Coordinación y Nutrición',
    image: '/Beatriz C - Ekkofood .jpg',
    quote: 'La salud comienza en la tierra y se refleja en cada alimento que cultivamos y compartimos',
    shortBio: 'Médica pediatra jubilada, con amplia trayectoria en nutrición y pediatría.',
    fullBio: 'Experta en nutrición y agricultura ecológica aplicada a la alimentación saludable. Aplica técnicas sostenibles para fomentar una agricultura regenerativa y ecológica. Supervisa menús y prácticas culinarias en retiros y eventos.',
    detailedBio: 'Médica pediatra de formación, Beatriz combina su conocimiento científico con su experiencia en agricultura ecológica. Se involucró plenamente en la agricultura sostenible tras observar efectos adversos de alimentos convencionales en sus hijos.\n\nDiseña y supervisa menús nutritivos para retiros y eventos, asegurando que cada comida sea equilibrada y deliciosa, y aplica técnicas ecológicas en la finca mediante asociaciones de plantas y experimentos de cultivo.',
    stats: [
      {
        title: 'Agricultura Sostenible',
        description: 'Aplica técnicas ecológicas y regenerativas en los cultivos de la finca.',
        icon: <AgricultureIcon sx={{ color: '#455b40' }} />
      },
      {
        title: 'Nutrición y Menús',
        description: 'Diseña y supervisa menús equilibrados para retiros y eventos.',
        icon: <WorkspacePremiumIcon sx={{ color: '#455b40' }} />
      }
    ],
    achievements: [
      'Jefa del Departamento de Nutrición en Lima, Perú',
      'Experta en técnicas de agricultura ecológica aplicada a la alimentación',
      'Diseño y supervisión de menús para retiros y eventos'
    ],
    expertise: ['Agricultura Ecológica', 'Nutrición', 'Técnicas de Cultivo Sostenible'],
    yearsOfExperience: 'Coordinación y Nutrición'
  },
  {
    id: 3,
    slug: 'beatriz-llerena',
    name: 'Beatriz Llerena',
    role: 'Cofundadora y Estrategia',
    image: '/Beatriz LL - Ekkofood1.jpg',
    quote: 'Aporto mi energía y conocimiento para que cada proyecto genere impacto real en la vida de las personas',
    shortBio: 'Líder en coordinación de operaciones y gestión de cursos. Organiza retiros de bienestar y experiencias educativas. Encabeza la comunicación y el próximo podcast de Ekkofood.',
    fullBio: 'Líder en coordinación de operaciones y gestión de cursos. Organiza retiros de bienestar y experiencias educativas. Encabeza la comunicación y el próximo podcast de Ekkofood.',
    detailedBio: 'Beatriz coordina todas las operaciones de Ekkofood y la realización de cursos y talleres desde el año 2000. Su carrera profesional incluyó cinco años en Asia, sin que esto interfiriera con la gestión remota de la finca. Desde 2018 organiza retiros y eventos de bienestar, que combinan alimentación saludable y atención al cuerpo y la mente.\n\nAdemás, gestiona la estrategia de comunicación de Ekkofood y liderará el lanzamiento del podcast oficial de Ekkofood en 2026, como parte de su labor de difusión y educación sobre agricultura ecológica y sostenible.',
    linkedIn: 'https://www.linkedin.com/in/beatrizllerena/',
    stats: [
      {
        title: 'Gestión Integral',
        description: 'Supervisa operaciones, procesos y coordinación del equipo en Ekkofood.',
        icon: <SchoolIcon sx={{ color: '#455b40' }} />
      },
      {
        title: 'Retiros y Bienestar',
        description: 'Organiza experiencias que combinan alimentación saludable y cuidado personal.',
        icon: <AgricultureIcon sx={{ color: '#455b40' }} />
      }
    ],
    achievements: [
      'Coordinación integral de Ekkofood desde 2000',
      'Organización de retiros y eventos de bienestar',
      'Experiencia en gestión remota internacional',
      'Preparación y lanzamiento del podcast oficial de Ekkofood en 2026'
    ],
    expertise: ['Gestión de Operaciones', 'Coordinación de Cursos', 'Organización de Retiros y Eventos', 'Estrategia de Comunicación'],
    yearsOfExperience: 'Cofundadora y Estrategia'
  },
  {
    id: 4,
    slug: 'jose-llerena',
    name: 'José Llerena',
    role: 'Producción y Mantenimiento',
    image: '/José - Ekkofood.jpg',
    quote: 'Cuidar la tierra y cada cultivo con detalle garantiza alimentos de calidad y sostenibles.',
    shortBio: 'Responsable de producción ecológica y mantenimiento de la finca. Supervisa cultivos, distribución de productos y optimización de procesos. Aplica creatividad y atención al detalle para soluciones sostenibles diarias.',
    fullBio: 'Responsable de producción ecológica y mantenimiento de la finca. Supervisa cultivos, distribución de productos y optimización de procesos. Aplica creatividad y atención al detalle para soluciones sostenibles diarias.',
    detailedBio: 'José supervisa la producción y el mantenimiento de la finca, así como la distribución de los productos ecológicos. Se encarga de 500 árboles de granado ecológico, asegurando calidad y sostenibilidad, y aplica soluciones innovadoras para optimizar los procesos diarios.',
    stats: [
      {
        title: 'Producción Ecológica',
        description: 'Supervisa cultivos sostenibles y asegura la calidad de los productos.',
        icon: <AgricultureIcon sx={{ color: '#455b40' }} />
      },
      {
        title: 'Innovación y Detalle',
        description: 'Aplica creatividad y atención al detalle en la gestión de la finca.',
        icon: <VerifiedIcon sx={{ color: '#455b40' }} />
      }
    ],
    achievements: [
      'Supervisión de 500 árboles de granado ecológico',
      'Optimización de procesos de producción y mantenimiento',
      'Coordinación de distribución de productos frescos'
    ],
    expertise: ['Producción Ecológica', 'Mantenimiento de Finca', 'Logística y Distribución'],
    yearsOfExperience: 'Producción y Mantenimiento'
  }
];

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6, 0),
  background: '#f8f9fa',
  minHeight: '80vh',
}));

const MainContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  maxWidth: '1400px',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  width: '350px',
  flexShrink: 0,
  position: 'sticky',
  top: theme.spacing(3),
  height: 'fit-content',
  minHeight: '600px',
  background: '#ffffff',
  borderRadius: theme.spacing(3),
  overflow: 'hidden',
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
  [theme.breakpoints.down('md')]: {
    position: 'relative',
    width: '320px',
    alignSelf: 'center',
    top: 'auto',
    minHeight: '500px',
  },
}));

const ImageSection = styled(Box)(({ theme }) => ({
  background: '#f5f5f5',
  padding: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: 'auto',
}));

const ContentSection2 = styled(Box)(({ theme }) => ({
  background: '#445c44',
  padding: theme.spacing(3),
  textAlign: 'center',
}));

const RightContent = styled(Box)(({ theme }) => ({
  flex: 1,
  background: '#ffffff',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(4),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
  },
}));

const ProfileImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: 'auto',
  maxHeight: '400px',
  objectFit: 'contain',
  objectPosition: 'center',
  display: 'block',
  borderRadius: 0,
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

const BadgeChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(124, 179, 66, 0.1)',
  color: 'theme.palette.primary.main',
  fontWeight: 600,
  borderRadius: '20px',
  padding: theme.spacing(0.5, 1),
}));



const StatIcon = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  backgroundColor: 'rgba(124, 179, 66, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
}));

const SectionCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  border: '1px solid rgba(194, 184, 168, 0.15)',
  boxShadow: '0 6px 20px rgba(194, 184, 168, 0.1)',
  marginBottom: theme.spacing(3),
}));

const ContactButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#455b40',
  color: '#ffffff',
  padding: theme.spacing(1.5, 3),
  borderRadius: '25px',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#74886b',
    boxShadow: '0 4px 12px rgba(124, 179, 66, 0.3)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.3s ease',
}));

const PersonDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const member = teamMembers.find(m => m.slug === slug);

  if (!member) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h4" gutterBottom>
          Miembro del equipo no encontrado
        </Typography>
        <Button onClick={() => navigate('/quienes-somos')}>
          Volver al equipo
        </Button>
      </Box>
    );
  }

  return (
    <>
      
      
      <PageHero 
        title={member.name}
        subtitle={member.role}
        description={`Conoce más sobre ${member.name}, ${member.role.toLowerCase()} de Ekkofood`}
        backgroundImage="https://images.unsplash.com/photo-1544027993-37dbfe43562a?q=80&w=2070"
        height="400px"
        showButton={false}
        contentAlign="center"
        overlay={true}
        overlayOpacity={0.4}
        showBreadcrumbs={true}
        breadcrumbs={[
          { label: "Inicio", link: "/" },
          { label: "Quiénes Somos", link: "/quienes-somos" },
          { label: member.name, link: `/equipo/${member.slug}` }
        ]}
      />
      
      <ContentSection>
        <MainContainer>
          {/* Left Panel with Profile */}
          <LeftPanel>
            {/* Image Section with Light Background */}
            <ImageSection>
              <ProfileImage 
                src={member.image} 
                alt={member.name}
              />
            </ImageSection>
            
            {/* Content Section with Dark Background */}
            <ContentSection2>
              {member.badge && (
                <BadgeChip 
                  label={member.badge.text} 
                  size="small"
                  sx={{ 
                    mb: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    color: '#ffffff',
                    fontWeight: 600
                  }}
                />
              )}
              
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 1,
                  color: '#ffffff',
                  fontSize: { xs: '1.5rem', md: '1.8rem' },
                  lineHeight: 1.2,
                }}
              >
                {member.name}
              </Typography>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#ffffff',
                  fontWeight: 600,
                  mb: 2,
                  fontSize: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {member.role}
              </Typography>
              
              {member.quote && (
                <Box sx={{ position: 'relative', mb: 3 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontStyle: 'italic',
                      lineHeight: 1.4,
                      color: 'rgba(255, 255, 255, 0.85)',
                      fontSize: '1rem',
                      textAlign: 'center',
                    }}
                  >
                    "{member.quote}"
                  </Typography>
                </Box>
              )}
              
              <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', mb: 2 }} />
              
              <Stack spacing={1} alignItems="center">
                {member.linkedIn && (
                  <ContactButton 
                    startIcon={<LinkedInIcon />}
                    onClick={() => window.open(member.linkedIn, '_blank', 'noopener,noreferrer')}
                    size="small"
                    sx={{ 
                      backgroundColor: 'rgba(124, 179, 66, 0.9)',
                      color: '#ffffff',
                      fontSize: '0.8rem',
                      '&:hover': { 
                        backgroundColor: 'rgba(124, 179, 66, 1)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    LinkedIn
                  </ContactButton>
                )}
                <ContactButton 
                  startIcon={<EmailIcon />}
                  onClick={() => window.location.href = `mailto:${member.slug === 'beatriz-llerena' ? 'beatriz@ekkofood.com' : 'info@ekkofood.com'}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: '#ffffff',
                    fontSize: '0.75rem',
                    '&:hover': {
                      borderColor: '#ffffff',
                      backgroundColor: '#74886b',
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  {member.slug === 'beatriz-llerena' ? 'beatriz@ekkofood.com' : 'info@ekkofood.com'}
                </ContactButton>
              </Stack>
            </ContentSection2>
          </LeftPanel>

          {/* Right Content Panel */}
          <RightContent>
            {/* Back Button */}
            <Box sx={{ mb: 4 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/quienes-somos')}
                sx={{ 
                  color: '#455b40',
                  '&:hover': { backgroundColor: 'rgba(124, 179, 66, 0.1)' }
                }}
              >
                Volver al equipo
              </Button>
            </Box>

            {/* Header */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="overline" 
                sx={{ 
                  color: '#455b40',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  letterSpacing: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2
                }}
              >
                <LocalFloristIcon sx={{ fontSize: '1rem' }} />
                {member.yearsOfExperience}
              </Typography>
              
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 2,
                  fontSize: { xs: '1.8rem', md: '2.2rem' },
                  color: 'text.primary',
                  lineHeight: 1.2
                }}
              >
                {member.name}
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  lineHeight: 1.6, 
                  color: 'text.secondary',
                  fontSize: '1rem',
                  mb: 4
                }}
              >
                {member.fullBio}
              </Typography>
            </Box>

            {/* Content Sections */}
            <Stack spacing={3}>
              {/* Detailed Biography */}
              <SectionCard>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <StarIcon sx={{ color: '#455b40' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Historia Profesional
                  </Typography>
                </Stack>
                {member.detailedBio.split('\n\n').map((paragraph, index) => (
                  <Typography 
                    key={index}
                    variant="body2" 
                    sx={{ 
                      lineHeight: 1.6, 
                      color: 'text.secondary',
                      fontSize: '0.95rem',
                      mb: index < member.detailedBio.split('\n\n').length - 1 ? 2 : 0
                    }}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </SectionCard>

              {/* Stats and Expertise */}
              {member.stats && (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                  {/* Stats */}
                  <Box sx={{ flex: 1 }}>
                    <SectionCard>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                        Áreas de Impacto
                      </Typography>
                      <Stack spacing={2}>
                        {member.stats.map((stat, index) => (
                          <Box key={index}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                              <StatIcon sx={{ width: 32, height: 32 }}>
                                {stat.icon}
                              </StatIcon>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 600,
                                  fontSize: '0.9rem',
                                  color: 'text.primary'
                                }}
                              >
                                {stat.title}
                              </Typography>
                            </Stack>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'text.secondary',
                                lineHeight: 1.5,
                                pl: 5,
                                fontSize: '0.85rem'
                              }}
                            >
                              {stat.description}
                            </Typography>
                            {index < member.stats!.length - 1 && (
                              <Divider sx={{ mt: 1.5 }} />
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </SectionCard>
                  </Box>

                  {/* Expertise */}
                  <Box sx={{ flex: 1 }}>
                    {member.expertise && (
                      <SectionCard>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                          <SchoolIcon sx={{ color: '#455b40' }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Especialidades
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {member.expertise.map((skill, index) => (
                            <Chip
                              key={index}
                              label={skill}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(124, 179, 66, 0.1)',
                                color: '#455b40',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                '&:hover': {
                                  backgroundColor: 'rgba(124, 179, 66, 0.2)',
                                }
                              }}
                            />
                          ))}
                        </Stack>
                      </SectionCard>
                    )}
                  </Box>
                </Stack>
              )}

              {/* Achievements */}
              {member.achievements && (
                <SectionCard>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <WorkspacePremiumIcon sx={{ color: '#455b40' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Logros Destacados
                    </Typography>
                  </Stack>
                  <Stack spacing={2}>
                    {member.achievements.map((achievement, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'primary.main',
                            mt: 0.5,
                            flexShrink: 0
                          }}
                        />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            lineHeight: 1.5, 
                            color: '#FFFFFFF',
                            fontSize: '0.9rem'
                          }}
                        >
                          {achievement}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </SectionCard>
              )}
            </Stack>
          </RightContent>
        </MainContainer>
      </ContentSection>
      
      {/* Team Carousel */}
      <TeamCarousel 
        members={teamMembers}
        currentMemberId={member.id}
        title="Otros Miembros del Equipo"
      />
      
      <Footer />
    </>
  );
};

export default PersonDetail;